<script setup lang="ts">
import { computed, ref, watch } from 'vue';

// EMITS
const emit = defineEmits<{
  (e: 'update', newValue: object | null): void;
}>();

// PROPS
const props = withDefaults(
  defineProps<{
    options: object[];
    defaultValue?: object | null;
    id?: string;
    name?: string;
    ariaLabel?: string;
    ariaLabelledby?: string;
  }>(),
  {
    defaultValue: null,
  },
);

// REFS
const modelValue = ref<object | null>(null);

// COMPUTED
const localModelValue = computed({
  get() {
    return modelValue.value;
  },
  set(newValue) {
    modelValue.value = newValue;
    emit('update', newValue);
  },
});

const selectAttrs = computed(() => {
  const attrs: Record<string, string> = {};

  if (props.id) {
    attrs.id = props.id;
  }

  if (props.name) {
    attrs.name = props.name;
  }

  if (props.ariaLabel) {
    attrs['aria-label'] = props.ariaLabel;
  }

  if (props.ariaLabelledby) {
    attrs['aria-labelledby'] = props.ariaLabelledby;
  }

  return attrs;
});

watch(
  () => props.defaultValue,
  (newValue) => {
    modelValue.value = newValue ?? null;
  },
  { immediate: true },
);
</script>

<template>
  <q-select
    class="base-select full-width"
    v-model="localModelValue"
    :options="props.options"
    outlined
    v-bind="selectAttrs"
  />
</template>

<style lang="scss">
.base-select {
  min-width: 270px;

  .items-center {
    height: 44px;
  }
}

.option {
  &__name {
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.base-select.q-field {
  .q-field__native {
    line-height: 9px;
    align-items: inherit;

    // Since quasar 2.18 the selected value is an .ellipsis span, which clips its
    // glyphs at this line-height. Render it as the bare span it was before.
    > .ellipsis {
      overflow: visible;
      white-space: normal;
    }
  }

  .q-field__control {
    background-color: $color-neutral-white;
    padding: 0 6px 0 12px;
    min-height: 44px;
    height: 44px;

    &:before {
      border: none;
    }

    &-container {
      align-items: center;
    }
  }

  .q-field__input {
    position: relative;
    bottom: 1px;
  }
}

.q-item.q-item-type {
  padding: 8px;
  height: 44px;
  min-height: 44px;
}
</style>
